import { QueryClient } from '@tanstack/react-query';
import { ROADMAPPER_WORKSPACE_STORAGE_KEY } from './workspace';

// Shared React Query client for the protected product surface. Cached data is
// rendered immediately on revisit, then refreshed in the background once it is
// stale so work performed by another person or agent becomes visible without a
// blocking route skeleton.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: (failureCount, error: Error & { status?: number }) => {
        if (error?.status !== undefined && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 15000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Every API request is workspace-scoped via headers read from this storage key
// (see roadmapper-api-client-base.ts), so cached data is only valid for the
// workspace it was fetched under. Namespacing every query key by the raw
// stored value keeps a workspace switch from serving another workspace's data.
export function getWorkspaceScope(): string {
  try {
    return window.localStorage.getItem(ROADMAPPER_WORKSPACE_STORAGE_KEY) ?? 'default';
  } catch {
    return 'default';
  }
}

export interface DashboardCacheScope { userId: string; organizationId: string; workspaceId: string }
export function dashboardScopeKey(scope: DashboardCacheScope) {
  return ['work-dashboard', scope.userId, scope.organizationId, scope.workspaceId] as const;
}
export const queryKeys = {
  projects: {
    all: (ws: string) => ['projects', ws] as const,
    list: (ws: string) => [...queryKeys.projects.all(ws), 'list'] as const,
    detail: (ws: string, id: string) => [...queryKeys.projects.all(ws), 'detail', id] as const,
    dashboard: (ws: string, id: string) => [...queryKeys.projects.all(ws), 'dashboard', id] as const,
    planningFeed: (ws: string, id: string) => [...queryKeys.projects.all(ws), 'planning-feed', id] as const,
  },
  roadmaps: {
    all: (ws: string) => ['roadmaps', ws] as const,
    list: (ws: string, projectId?: string) =>
      [...queryKeys.roadmaps.all(ws), 'list', projectId ?? 'all'] as const,
    detail: (ws: string, id: string) => [...queryKeys.roadmaps.all(ws), 'detail', id] as const,
    dependencies: (ws: string, id: string) => [...queryKeys.roadmaps.all(ws), 'dependencies', id] as const,
    attachments: (ws: string, id: string) =>
      [...queryKeys.roadmaps.all(ws), 'attachments', id] as const,
  },
  roadmapItems: {
    all: (ws: string) => ['roadmap-items', ws] as const,
    byRoadmap: (ws: string, roadmapId: string) =>
      [...queryKeys.roadmapItems.all(ws), 'roadmap', roadmapId] as const,
    detail: (ws: string, id: string) => [...queryKeys.roadmapItems.all(ws), 'detail', id] as const,
    dependencies: (ws: string, itemId: string) =>
      [...queryKeys.roadmapItems.all(ws), 'dependencies', itemId] as const,
    comments: (ws: string, itemId: string) =>
      [...queryKeys.roadmapItems.all(ws), 'comments', itemId] as const,
    attachments: (ws: string, itemId: string, includeDescendants = false) =>
      [...queryKeys.roadmapItems.all(ws), 'attachments', itemId, includeDescendants] as const,
  },
  presentations: {
    all: (ws: string) => ['presentations', ws] as const,
    detail: (ws: string, id: string) =>
      [...queryKeys.presentations.all(ws), 'detail', id] as const,
  },
  execution: {
    all: (ws: string) => ['execution', ws] as const,
    forecast: (ws: string, roadmapId: string, profile?: string) =>
      [...queryKeys.execution.all(ws), 'forecast', roadmapId, ...(profile ? [profile] : [])] as const,
  },
} as const;
